import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';

import { useAuth } from '@restworld/feature-authentication';
import { useRouterBackCallback } from '@restworld/utils-common';
import { useWizard } from '@restworld/utils-common';
import { useJobPositionsService } from '@restworld/data-services';
import { ModalMessage } from '@restworld/ui-components';
import type { JobApplication, JobPosition } from '@restworld/utility-types';

import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';
import { useViolaBadge, useApplyJob } from '../../../../hooks';
import { wizardCreatedApplicationAtom } from '../../../worker-managed-using-klaaryo';

export const useAutoApplyJob = () => {
  const { user } = useAuth();
  const { activeStep } = useWizard();
  const router = useRouter();
  const query = router?.['query'];
  const { onRouterBack } = useRouterBackCallback(`/profilo`);
  const { setOverlayModalContent, hasAppliedJob, setHasAppliedJob, userModifying } = useWorkerApplicationCxt();
  const jobPositionService = useJobPositionsService();
  const { t } = useTranslation();
  const isSpontaneousEntry = useMemo(() => query?.['isSpontaneousEntry'] === 'true', [query]);
  const workerId = useMemo(() => user?.id, [user?.id]);
  const queryId = useMemo(() => query?.['id'], [query]);

  const setWizardCreatedApplication = useSetAtom(wizardCreatedApplicationAtom);
  const { fetchShowViolaBadge } = useViolaBadge(undefined, { initOnMount: false });

  // The wizard's own position, read once: the confirmation at the end of the wizard needs its
  // ATS fields, and the page-wide `jobPositionApiDataAtom` is not about this position here.
  const jobPositionRequestRef = useRef<Promise<Partial<JobPosition> | undefined> | null>(null);

  const initViolaBadge = useCallback(() => {
    if (!queryId || isSpontaneousEntry) return;
    const request = jobPositionService.showJobPosition(queryId as string);
    jobPositionRequestRef.current = request.then(
      (res) => res.data,
      () => undefined
    );
    request
      .then((res) => {
        fetchShowViolaBadge(res.data?.id);
      })
      .catch((error) => {
        console.error(error, `Error while fetching job position data for id: ${queryId}`);
      });
  }, [jobPositionService, queryId, isSpontaneousEntry, fetchShowViolaBadge]);

  useEffect(() => {
    initViolaBadge();
  }, [initViolaBadge]);

  const { applyJobPosition } = useApplyJob({
    referralCode: query?.['scout'],
    onAuthenticated: () =>
      setOverlayModalContent({
        visible: true,
        content: (
          <ModalMessage
            title={t('common:application_delivered')}
            subTitle={t('common:confirm_if_everything_updated')}
            closeButtonTitle={t('common:close')}
            onCloseCallback={onRouterBack}
            redirectBtnTitle={t('common:check_your_profile')}
            onRedirectCallback={() => router?.push('/profilo')}
          />
        ),
        key: 'auto-apply-job',
      }),
  });
  // Synchronous guard: `setHasAppliedJob(true)` only runs after the await, so
  // without this flag concurrent effect re-renders fire parallel POSTs that
  // hit the klaaryo_conversations pkey race on the backend (AppSignal #271).
  const isApplyingRef = useRef(false);
  const onApplyJob = useCallback(async () => {
    if (isApplyingRef.current) return;
    try {
      if (workerId && queryId && !hasAppliedJob && activeStep === 0) {
        isApplyingRef.current = true;
        const created = await applyJobPosition({
          workerIdArg: workerId,
          [isSpontaneousEntry ? 'restaurantId' : 'jobPositionId']: queryId,
          source: 'wizard',
        });
        // Read on `/profilo?manage_using_klaaryo=true`, where the wizard ends, to pick the
        // confirmation for this application's interview channel. A spontaneous application
        // has no interview.
        if (!isSpontaneousEntry) {
          const application = (created as { job_application?: Partial<JobApplication> })?.job_application;
          const jobPositionRequest =
            jobPositionRequestRef.current ??
            jobPositionService.showJobPosition(queryId as string).then(
              (res) => res.data,
              () => undefined
            );
          // Not awaited: the wizard goes on; the profile reads this minutes later.
          void jobPositionRequest.then((jobPosition) =>
            setWizardCreatedApplication({
              application,
              jobPosition: {
                ats_applyurl: jobPosition?.ats_applyurl,
                published_from_ats: !!jobPosition?.published_from_ats,
              },
            })
          );
        }
        setHasAppliedJob(true);
      }
    } catch (error) {
      console.error('error on auto applying', error);
      // trackEvent('Worker onboarding', { phase: 'user gets error on auto applying job on step 1' });
    } finally {
      isApplyingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, no-sparse-arrays
    // }, [workerId, queryId, hasAppliedJob, activeStep, isSpontaneousEntry, setHasAppliedJob, trackEvent]);
  }, [
    workerId,
    queryId,
    hasAppliedJob,
    activeStep,
    isSpontaneousEntry,
    setHasAppliedJob,
    applyJobPosition,
    setWizardCreatedApplication,
    jobPositionService,
  ]);
  useEffect(() => {
    if (!userModifying) onApplyJob();
  }, [onApplyJob, userModifying]);
};
